import React, { useState } from "react";
import {
  GATES,
  BLOCKS,
  isSequential,
  pins,
  sourceKey,
  signalName,
  LABELS,
  defaults,
  CLOCK_MODES,
} from "../engine/registry";
export function Field({ label, children, hint }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}
export function ConfigFields({ node, update, fixedClockTiming = false }) {
  const c = node.config,
    edit = (key, value) => update({ [key]: value });
  return (
    <>
      {GATES.includes(node.type) && node.type !== "NOT" && (
        <Field label="Input pins">
          <input
            type="number"
            min="2"
            max="8"
            value={c.inputs}
            onChange={(e) => edit("inputs", +e.target.value)}
          />
        </Field>
      )}
      {["MUX", "DEMUX", "ENCODER", "PRIORITY", "DECODER"].includes(
        node.type,
      ) && (
        <Field label={node.type === "DECODER" ? "Address bits" : "Size"}>
          <select
            value={c.size}
            onChange={(e) => edit("size", +e.target.value)}
          >
            {(node.type === "DECODER"
              ? [2, 3]
              : ["ENCODER", "PRIORITY"].includes(node.type)
                ? [4, 8]
                : [2, 4, 8]
            ).map((n) => (
              <option key={n} value={n}>
                {node.type === "DECODER"
                  ? `${n} → ${2 ** n}`
                  : node.type === "MUX"
                    ? `${n} → 1`
                    : node.type === "DEMUX"
                      ? `1 → ${n}`
                      : n}
              </option>
            ))}
          </select>
        </Field>
      )}
      {["PA", "COMPARE"].includes(node.type) && (
        <Field label="Bit width">
          <select
            value={c.width}
            onChange={(e) => edit("width", +e.target.value)}
          >
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <option key={n}>{n}</option>
            ))}
          </select>
        </Field>
      )}
      {node.type === "DECODER" && (
        <label className="check">
          <input
            type="checkbox"
            checked={c.enabled}
            onChange={(e) => edit("enabled", e.target.checked)}
          />
          Enable input
        </label>
      )}
      {node.type === "PA" && (
        <label className="check">
          <input
            type="checkbox"
            checked={c.expanded}
            onChange={(e) => edit("expanded", e.target.checked)}
          />
          Show full-adder chain
        </label>
      )}
      {isSequential(node) && (
        <>
          <Field label="Output variable">
            <input
              value={c.output}
              placeholder={node.name}
              onChange={(e) => edit("output", e.target.value)}
              maxLength="24"
            />
          </Field>
          <div className="field-pair">
            <Field label="Initial Q">
              <select
                value={c.initial}
                onChange={(e) =>
                  edit(
                    "initial",
                    e.target.value === "X" ? "X" : +e.target.value,
                  )
                }
              >
                {[0, 1, "X"].map((v) => (
                  <option key={v}>{v}</option>
                ))}
              </select>
            </Field>
          </div>
          {node.type.startsWith("SR") && (
            <Field
              label="When S = R = 1"
              hint="Active-high S/R. A classroom assumption, not a physical hardware guarantee."
            >
              <select
                value={c.policy}
                onChange={(e) => edit("policy", e.target.value)}
              >
                <option value="X">Unknown (X)</option>
                <option value="0">Force Q = 0</option>
                <option value="1">Force Q = 1</option>
                <option value="HOLD">Hold previous Q</option>
              </select>
            </Field>
          )}
        </>
      )}
      {node.type === "CLOCK" && (
        <>
          <Field label="Clock type">
            <select
              value={c.activation || "POS_EDGE"}
              onChange={(e) => edit("activation", e.target.value)}
            >
              {Object.entries(CLOCK_MODES).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          {!fixedClockTiming && <div className="field-pair">
            <Field label="Clock period" hint="Time for one complete low–high cycle. A period of 4 has two 2-unit halves at 50% duty.">
              <input
                type="number"
                min="0.1"
                step="0.1"
                aria-label="Clock period"
                value={c.period}
                onChange={(e) => edit("period", +e.target.value)}
              />
            </Field>
            <Field label="Duty (%)">
              <input
                type="number"
                min="1"
                max="99"
                value={Math.round(c.duty * 100)}
                onChange={(e) => edit("duty", +e.target.value / 100)}
              />
            </Field>
          </div>}
          <div className={fixedClockTiming ? undefined : "field-pair"}>
            {!fixedClockTiming && <Field label="Start delay (phase)" hint="Time to hold the clock at its initial level before the first cycle begins. Use 0 for no delay.">
              <input
                type="number"
                min="0"
                step="0.1"
                aria-label="Start delay (phase)"
                value={c.phase}
                onChange={(e) => edit("phase", +e.target.value)}
              />
            </Field>}
            <Field label="Clock initial level">
              <select
                value={c.initial}
                onChange={(e) => edit("initial", +e.target.value)}
              >
                <option value="0">0 · low</option>
                <option value="1">1 · high</option>
              </select>
            </Field>
          </div>
          <p className="helper">
            Connected devices inherit the edge or active level from this clock.
          </p>
        </>
      )}
      {node.type === "ENCODER" && (
        <p className="helper">
          Exactly one input must be 1. Otherwise V = 0 and the code is X. D0 is
          the least significant index.
        </p>
      )}
      {node.type === "PRIORITY" && (
        <p className="helper">
          The highest numbered asserted D input wins. V = 0 with code 0 when all
          inputs are 0.
        </p>
      )}
      {BLOCKS.includes(node.type) && (
        <p className="helper">
          Bit 0 is the least significant bit. Selectors S0 / address A0 have
          weight 1.
        </p>
      )}
    </>
  );
}
export default function Properties({
  project,
  selected,
  selectedWire,
  updateComponent,
  onConnect,
  onDisconnect,
  onDelete,
  onDuplicate,
  onObserve,
  issues,
  onSelect,
}) {
  const c = project.components.find((c) => c.id === selected[0]),
    wire = project.connections.find((e) => e.id === selectedWire);
  const [tab, setTab] = useState("properties");
  const sources = [
    ...project.inputs.map((i) => ({
      value: `input:${i.id}`,
      label: i.name,
      source: { input: i.id },
    })),
    ...project.components.flatMap((n) =>
      pins(n).outputs.map((p) => ({
        value: `${n.id}:${p.id}`,
        label: signalName(project, { component: n.id, pin: p.id }),
        source: { component: n.id, pin: p.id },
      })),
    ),
  ];
  return (
    <aside className="inspector panel">
      <div className="panel-tabs">
        <button
          className={tab === "properties" ? "active" : ""}
          onClick={() => setTab("properties")}
        >
          Properties
        </button>
        <button
          className={tab === "validation" ? "active" : ""}
          onClick={() => setTab("validation")}
        >
          Checks <span className="count">{issues.length}</span>
        </button>
      </div>
      {tab === "validation" ? (
        <div className="panel-content">
          <p className="helper">
            Errors block simulation. Warnings explain connections worth
            reviewing.
          </p>
          {!issues.length && (
            <div className="success-note">All circuit checks passed.</div>
          )}
          {issues.map((i, n) => (
            <button
              key={n}
              className={`issue ${i.severity}`}
              onClick={() => i.component && onSelect(i.component)}
            >
              <b>{i.severity === "error" ? "Fix required" : "Review"}</b>
              <span>{i.message}</span>
            </button>
          ))}
        </div>
      ) : c ? (
        <div className="panel-content">
          <div className="eyebrow">
            {LABELS[c.type]}{" "}
            {selected.length > 1 && `· +${selected.length - 1} selected`}
          </div>
          <Field label="Instance name">
            <input
              value={c.name}
              maxLength="32"
              onChange={(e) => updateComponent(c.id, { name: e.target.value })}
            />
          </Field>
          {GATES.includes(c.type) && (
            <Field label="Replace gate">
              <select
                value={c.type}
                onChange={(e) =>
                  updateComponent(c.id, {
                    type: e.target.value,
                    config: {
                      ...c.config,
                      inputs:
                        e.target.value === "NOT"
                          ? 1
                          : Math.max(2, c.config.inputs),
                    },
                  })
                }
              >
                {GATES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </Field>
          )}
          {isSequential(c) && (
            <Field label="Replace sequential device">
              <select
                value={c.type}
                onChange={(e) =>
                  updateComponent(c.id, {
                    type: e.target.value,
                    config: { ...defaults(e.target.value), ...c.config },
                  })
                }
              >
                {["D_FF", "T_FF", "JK_FF", "SR_FF", "SR_LATCH"].map((t) => (
                  <option key={t} value={t}>
                    {LABELS[t]}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <ConfigFields
            node={c}
            update={(config) =>
              updateComponent(c.id, { config: { ...c.config, ...config } })
            }
          />
          <div className="section-rule">
            <span>Connections</span>
          </div>
          {pins(c).inputs.map((pin) => {
            const edge = project.connections.find(
              (e) => e.to.component === c.id && e.to.pin === pin.id,
            );
            return (
              <Field
                key={pin.id}
                label={pin.id === "EN" ? "EN · level control" : pin.id}
              >
                <select
                  aria-label={`${c.name} ${pin.id} source`}
                  value={edge ? sourceKey(edge.from) : ""}
                  onChange={(e) => {
                    const src = sources.find((s) => s.value === e.target.value);
                    if (src)
                      onConnect(src.source, { component: c.id, pin: pin.id });
                    else if (edge) onDisconnect(edge.id);
                  }}
                >
                  <option value="">Unconnected</option>
                  {sources.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </Field>
            );
          })}
          <div className="section-rule">
            <span>Observe outputs</span>
          </div>
          <div className="checks-wrap">
            {pins(c).outputs.map((pin) => {
              const key = `${c.id}:${pin.id}`;
              return (
                <label className="check" key={pin.id}>
                  <input
                    type="checkbox"
                    checked={project.observables.some(
                      (o) => sourceKey(o.source) === key,
                    )}
                    onChange={(e) =>
                      onObserve(
                        { component: c.id, pin: pin.id },
                        e.target.checked,
                      )
                    }
                  />
                  {pin.id === "Qn" ? "Q′" : pin.id}
                </label>
              );
            })}
          </div>
          <div className="button-row">
            <button onClick={onDuplicate}>Duplicate</button>
            <button className="danger-text" onClick={onDelete}>
              Delete
            </button>
          </div>
        </div>
      ) : wire ? (
        <div className="panel-content">
          <div className="eyebrow">WIRE</div>
          <h3>{signalName(project, wire.from)}</h3>
          <p className="helper">
            Connected to{" "}
            {project.components.find((c) => c.id === wire.to.component)?.name}.
            {wire.to.pin}. Connect a new source to this pin to replace this
            wire. Double-click the wire to branch its signal.
          </p>
          <Field label="Reconnect source">
            <select
              value={sourceKey(wire.from)}
              onChange={(e) => {
                const s = sources.find((s) => s.value === e.target.value);
                if (s) onConnect(s.source, wire.to);
              }}
            >
              {sources.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </Field>
          <button className="danger-text" onClick={() => onDisconnect(wire.id)}>
            Delete connection
          </button>
        </div>
      ) : (
        <div className="panel-content empty-inspector">
          <div className="select-symbol">↖</div>
          <h3>A closer look</h3>
          <p>Select a component to edit its settings and connections.</p>
          <dl>
            <dt>Connect</dt>
            <dd>Drag output to input, or click each pin.</dd>
            <dt>Branch</dt>
            <dd>Connect the same output again.</dd>
            <dt>Multiple selection</dt>
            <dd>Shift-click or Shift-drag a box.</dd>
            <dt>Pan / zoom</dt>
            <dd>Drag the canvas / scroll.</dd>
            <dt>Shortcuts</dt>
            <dd>Ctrl/Cmd Z, Y, C, V, D · Delete · F to fit.</dd>
          </dl>
        </div>
      )}
    </aside>
  );
}
